import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	inject,
	signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { TranslocoModule } from '@ngneat/transloco';
import {
	AppSubjectEnum,
	CreateVehicleOdometerTrackingDto,
	ValidationFunctionResult,
	VehicleActionEnum,
	VehicleOdometerChangeReasonEnum,
	vehicleValidators,
} from '@splitsecondconnect/ultramarine-shared';

import { BaseActionComponent } from '../../../../../shared/base-classes/base-action.component';
import { ActionPortalContainerComponent } from '../../../../../shared/components/containers/action-portal-container/action-portal-container.component';
import { DatetimePickerComponent } from '../../../../../shared/components/datetime-picker/datetime-picker.component';
import { SingleDropdownComponent } from '../../../../../shared/components/dropdowns/single-dropdown/single-dropdown.component';
import { FreeFormInputComponent } from '../../../../../shared/components/free-form-input/free-form-input.component';
import { InputFieldComponent } from '../../../../../shared/components/input-field/input-field.component';
import {
	dateCast,
	toDatetimeLocalString,
} from '../../../../../shared/functions/sectionCasters.function';
import { VEHICLE_ODOMETER_VALUE_CHANGE_REASON } from '../../../vehicle-road-worthy-checks/translations/vehicle-road-worthy-checks-select-items';
import { VehiclesService } from '../../vehicles.service';

@Component({
	selector: 'app-update-odometer-task',
	templateUrl: 'update-odometer-task.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: {
		'[class]': 'modalClass()',
	},
	imports: [
		InputFieldComponent,
		FreeFormInputComponent,
		SingleDropdownComponent,
		TranslocoModule,
		DatetimePickerComponent,
		ActionPortalContainerComponent,
		ReactiveFormsModule,
	],
})
export class UpdateOdometerTaskComponent extends BaseActionComponent {
	odometerValueChangeReason = VEHICLE_ODOMETER_VALUE_CHANGE_REASON;
	private _vehiclesService = inject(VehiclesService);

	public recordDataSignal = signal(null);
	public validationErrors: { [key: string]: string[] } = {};
	private validationResultSignal = signal<ValidationFunctionResult>(null);

	public taskFormGroup = this._formBuilder.group({
		odometerValue: [null],
		odometerRecordedAt: [toDatetimeLocalString(new Date())],
		odometerUpdateReason: [VehicleOdometerChangeReasonEnum.INITIAL],
		notes: [null],
	});

	constructor(
		private _formBuilder: FormBuilder,
		private _cdr: ChangeDetectorRef,
	) {
		super();
		this.taskFormGroup.valueChanges
			.pipe(takeUntilDestroyed())
			.subscribe((res) => {
				const newData: CreateVehicleOdometerTrackingDto = {
					vehicleId: this.selectionIds[0],
					sourceSubject: AppSubjectEnum.VEHICLE,
					sourceId: this.selectionIds[0],
					recordedAt: dateCast(res.odometerRecordedAt),
					value: res.odometerValue,
					changeReason: res.odometerUpdateReason,
					notes: res.notes,
				};
				this.updateValidationResultSignal(newData);
			});
	}

	public submitTask() {
		const newData: CreateVehicleOdometerTrackingDto = {
			vehicleId: this.selectionIds[0],
			sourceSubject: AppSubjectEnum.VEHICLE,
			sourceId: this.selectionIds[0],
			recordedAt: dateCast(
				this.taskFormGroup.controls.odometerRecordedAt.value,
			),
			value: this.taskFormGroup.controls.odometerValue.value,
			changeReason: this.taskFormGroup.controls.odometerUpdateReason.value,
			notes: this.taskFormGroup.controls.notes.value,
		};

		this.updateValidationResultSignal(newData);

		if (!this.validationResultSignal().isValid) {
			return;
		}

		this.isSaving = true;
		this._vehiclesService.updateOdometer(newData.vehicleId, newData).subscribe({
			next: () => {
				this.successfullySubmittedTask$.next(null);
				this.onSuccess.next(null);
				this.onClose.next(null);
				this.clearTask();
			},
			complete: () => {
				this.isSaving = false;
				this.nav().pop();
			},
		});
	}

	updateValidationResultSignal(data) {
		if (data) {
			this.validationResultSignal.set(
				vehicleValidators.canPerformAction(
					null,
					VehicleActionEnum.UPDATE_ODOMETER,
					data,
				),
			);
			this.errorMessages.set(this.validationResultSignal().errors);
		} else {
			this.errorMessages.set(null);
		}
	}
}
